import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Progress } from '../../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import DictationInput from '../../components/DictationInput';
import { 
  AlertTriangle, Shield, User, Activity, Calendar, CheckCircle,
  XCircle, Clock, TrendingUp, TrendingDown, Eye, Flag, Bell,
  Search, RefreshCw, ChevronRight, AlertCircle, Heart, Users
} from 'lucide-react';
import { formatDate } from '../../lib/utils';
import { toast } from 'sonner';

const RISK_COLORS = {
  critical: { bg: 'bg-red-100', text: 'text-red-700', border: 'border-red-500', icon: 'text-red-600' },
  high: { bg: 'bg-orange-100', text: 'text-orange-700', border: 'border-orange-500', icon: 'text-orange-600' },
  medium: { bg: 'bg-amber-100', text: 'text-amber-700', border: 'border-amber-500', icon: 'text-amber-600' },
  low: { bg: 'bg-sky-100', text: 'text-sky-700', border: 'border-sky-500', icon: 'text-sky-600' },
  none: { bg: 'bg-emerald-100', text: 'text-emerald-700', border: 'border-emerald-500', icon: 'text-emerald-600' },
};

const CATEGORY_ICONS = {
  check_ins: Clock,
  drug_tests: Heart,
  appointments: Calendar,
  curfew: Shield,
  engagement: Users,
  activity: Activity,
};

export default function RiskDashboardPage() {
  const { axiosAuth } = useAuth();
  const [dashboard, setDashboard] = useState(null);
  const [flags, setFlags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedParticipant, setSelectedParticipant] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [flagDialogOpen, setFlagDialogOpen] = useState(false);
  const [filterLevel, setFilterLevel] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [newFlag, setNewFlag] = useState({
    participant_id: '',
    reason: '',
    severity: 'medium',
    notes: '',
  });
  const api = axiosAuth();

  const fetchData = async () => {
    setLoading(true);
    try {
      const [dashboardRes, flagsRes] = await Promise.all([
        api.get('/risk/dashboard'),
        api.get('/risk/flags?status=active'),
      ]);
      setDashboard(dashboardRes.data);
      setFlags(flagsRes.data.flags || []);
    } catch (err) {
      toast.error('Failed to load risk dashboard data');
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const viewParticipantDetail = async (participantId) => {
    try {
      const res = await api.get(`/risk/assessment/${participantId}`);
      setSelectedParticipant(res.data);
      setDetailDialogOpen(true);
    } catch (err) {
      toast.error('Failed to load assessment');
    }
  };

  const handleFlagParticipant = async () => {
    if (!newFlag.participant_id || !newFlag.reason) {
      toast.error('Please select participant and provide reason');
      return;
    }
    try {
      const params = new URLSearchParams();
      params.append('reason', newFlag.reason);
      params.append('severity', newFlag.severity);
      if (newFlag.notes) params.append('notes', newFlag.notes);
      await api.post(`/risk/flag/${newFlag.participant_id}?${params.toString()}`);
      toast.success('Participant flagged');
      setFlagDialogOpen(false);
      setNewFlag({ participant_id: '', reason: '', severity: 'medium', notes: '' });
      fetchData();
    } catch (err) {
      toast.error('Failed to flag participant');
    }
  };

  const resolveFlag = async (flagId) => {
    try {
      await api.put(`/risk/flags/${flagId}/resolve`);
      toast.success('Flag resolved');
      fetchData();
    } catch (err) {
      toast.error('Failed to resolve flag');
    }
  };

  const getRiskColor = (level) => RISK_COLORS[level] || RISK_COLORS.none;

  const filteredParticipants = dashboard?.participants?.filter(p => {
    if (filterLevel !== 'all' && p.risk_level !== filterLevel) return false;
    if (searchQuery && !p.participant_name?.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    return true;
  }) || [];

  if (loading && !dashboard) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-3 border-orange-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="risk-dashboard-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-foreground flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
            <AlertTriangle className="text-orange-500" /> Risk Assessment Dashboard
          </h1>
          <p className="text-muted-foreground mt-1 text-sm">
            Early warning system for at-risk participants
          </p>
        </div>
        <div className="flex gap-2">
          <Button onClick={fetchData} variant="outline" className="rounded-full gap-2">
            <RefreshCw size={16} /> Refresh
          </Button>
          <Button onClick={() => setFlagDialogOpen(true)} className="bg-orange-500 hover:bg-orange-600 text-white rounded-full gap-2">
            <Flag size={16} /> Flag Participant
          </Button>
        </div>
      </div>

      {/* Summary Stats */}
      {dashboard?.summary && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card className={`border-0 shadow-sm rounded-2xl cursor-pointer hover:shadow-md transition-shadow ${filterLevel === 'all' ? 'ring-2 ring-teal-300' : ''}`} onClick={() => setFilterLevel('all')} data-testid="risk-stat-total">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center">
                  <Users className="text-slate-600" size={20} />
                </div>
              </div>
              <p className="text-2xl font-bold">{dashboard.summary.total}</p>
              <p className="text-xs text-muted-foreground">Total Participants</p>
            </CardContent>
          </Card>
          <Card className={`border-0 shadow-sm rounded-2xl border-l-4 border-l-red-500 cursor-pointer hover:shadow-md transition-shadow ${filterLevel === 'critical' ? 'ring-2 ring-red-300' : ''}`} onClick={() => setFilterLevel(filterLevel === 'critical' ? 'all' : 'critical')} data-testid="risk-stat-critical">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-10 h-10 rounded-xl bg-red-50 flex items-center justify-center">
                  <AlertTriangle className="text-red-600" size={20} />
                </div>
              </div>
              <p className="text-2xl font-bold text-red-600">{dashboard.summary.critical}</p>
              <p className="text-xs text-muted-foreground">Critical Risk</p>
            </CardContent>
          </Card>
          <Card className={`border-0 shadow-sm rounded-2xl border-l-4 border-l-orange-500 cursor-pointer hover:shadow-md transition-shadow ${filterLevel === 'high' ? 'ring-2 ring-orange-300' : ''}`} onClick={() => setFilterLevel(filterLevel === 'high' ? 'all' : 'high')} data-testid="risk-stat-high">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-10 h-10 rounded-xl bg-orange-50 flex items-center justify-center">
                  <AlertCircle className="text-orange-600" size={20} />
                </div>
              </div>
              <p className="text-2xl font-bold text-orange-600">{dashboard.summary.high}</p>
              <p className="text-xs text-muted-foreground">High Risk</p>
            </CardContent>
          </Card>
          <Card className={`border-0 shadow-sm rounded-2xl border-l-4 border-l-amber-500 cursor-pointer hover:shadow-md transition-shadow ${filterLevel === 'medium' ? 'ring-2 ring-amber-300' : ''}`} onClick={() => setFilterLevel(filterLevel === 'medium' ? 'all' : 'medium')} data-testid="risk-stat-medium">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center">
                  <Activity className="text-amber-600" size={20} />
                </div>
              </div>
              <p className="text-2xl font-bold text-amber-600">{dashboard.summary.medium}</p>
              <p className="text-xs text-muted-foreground">Medium Risk</p>
            </CardContent>
          </Card>
          <Card className={`border-0 shadow-sm rounded-2xl border-l-4 border-l-emerald-500 cursor-pointer hover:shadow-md transition-shadow ${filterLevel === 'low' ? 'ring-2 ring-emerald-300' : ''}`} onClick={() => setFilterLevel(filterLevel === 'low' ? 'all' : 'low')} data-testid="risk-stat-low">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center">
                  <CheckCircle className="text-emerald-600" size={20} />
                </div>
              </div>
              <p className="text-2xl font-bold text-emerald-600">{dashboard.summary.low + dashboard.summary.none}</p>
              <p className="text-xs text-muted-foreground">Low/No Risk</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Active Alerts Banner */}
      {dashboard?.alerts?.length > 0 && (
        <Card className="border-0 shadow-sm rounded-2xl bg-gradient-to-r from-red-50 to-orange-50 border-l-4 border-l-red-500">
          <CardContent className="p-4">
            <div className="flex items-center gap-3 mb-3">
              <Bell className="text-red-600" size={20} />
              <span className="font-semibold text-red-900">Active Alerts ({dashboard.alerts.length})</span>
            </div>
            <div className="space-y-2 max-h-40 overflow-y-auto">
              {dashboard.alerts.slice(0, 5).map((alert, idx) => (
                <div key={alert.id || `alert-${idx}`} className="flex items-center justify-between p-2 bg-white/80 rounded-lg">
                  <div className="flex items-center gap-3">
                    <Badge className={getRiskColor(alert.severity).bg + ' ' + getRiskColor(alert.severity).text}>
                      {alert.severity}
                    </Badge>
                    <div>
                      <span className="font-medium">{alert.participant_name}</span>
                      <span className="text-muted-foreground mx-2">•</span>
                      <span className="text-sm text-muted-foreground">{alert.factor}: {alert.detail}</span>
                    </div>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => viewParticipantDetail(alert.participant_id)} className="text-xs">
                    View <ChevronRight size={14} />
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <Tabs defaultValue="participants" className="space-y-4">
        <TabsList className="bg-muted/50 rounded-full p-1">
          <TabsTrigger value="participants" className="rounded-full data-[state=active]:bg-white data-[state=active]:shadow-sm px-4">
            All Participants
          </TabsTrigger>
          <TabsTrigger value="flags" className="rounded-full data-[state=active]:bg-white data-[state=active]:shadow-sm px-4">
            Manual Flags ({flags.length})
          </TabsTrigger>
        </TabsList>

        {/* Participants Tab */}
        <TabsContent value="participants" className="space-y-4">
          {/* Filters */}
          <Card className="border-0 shadow-sm rounded-2xl">
            <CardContent className="p-4">
              <div className="flex flex-col md:flex-row gap-3">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={16} />
                  <Input 
                    placeholder="Search by name..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9 rounded-xl"
                  />
                </div>
                <Select value={filterLevel} onValueChange={setFilterLevel}>
                  <SelectTrigger className="w-[180px] rounded-xl">
                    <SelectValue placeholder="Filter by risk..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Levels</SelectItem>
                    <SelectItem value="critical">Critical Only</SelectItem>
                    <SelectItem value="high">High Only</SelectItem>
                    <SelectItem value="medium">Medium Only</SelectItem>
                    <SelectItem value="low">Low Only</SelectItem>
                    <SelectItem value="none">No Risk</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Participants List */}
          {filteredParticipants.length === 0 ? (
            <Card className="border-0 shadow-sm rounded-2xl">
              <CardContent className="p-8 text-center">
                <Shield className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
                <p className="text-muted-foreground">No participants matching filter criteria.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {filteredParticipants.map(p => {
                const color = getRiskColor(p.risk_level);
                return (
                  <Card 
                    key={p.participant_id} 
                    className={`border-0 shadow-sm rounded-2xl hover:shadow-md transition-shadow cursor-pointer border-l-4 ${color.border}`}
                    onClick={() => viewParticipantDetail(p.participant_id)}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className={`w-12 h-12 rounded-xl ${color.bg} flex items-center justify-center`}>
                            <AlertTriangle className={color.icon} size={24} />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <h3 className="font-semibold">{p.participant_name}</h3>
                              <Badge className={`${color.bg} ${color.text}`}>{p.risk_level.toUpperCase()}</Badge>
                            </div>
                            <div className="flex items-center gap-4 mt-1 text-sm text-muted-foreground">
                              <span>Score: {p.risk_score}/{100}</span>
                              <span>•</span>
                              <span>{p.factor_count} risk factor{p.factor_count !== 1 ? 's' : ''}</span>
                            </div>
                            {p.risk_factors?.length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-2">
                                {p.risk_factors.slice(0, 3).map((f, idx) => (
                                  <Badge key={f.label || `rf-${idx}`} variant="outline" className="text-xs">
                                    {f.label}
                                  </Badge>
                                ))}
                                {p.risk_factors.length > 3 && (
                                  <Badge variant="outline" className="text-xs">+{p.risk_factors.length - 3}</Badge>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="w-24">
                            <Progress 
                              value={p.risk_percentage} 
                              className="h-2"
                            />
                          </div>
                          <Button variant="ghost" size="icon">
                            <Eye size={18} />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* Flags Tab */}
        <TabsContent value="flags" className="space-y-4">
          {flags.length === 0 ? (
            <Card className="border-0 shadow-sm rounded-2xl">
              <CardContent className="p-8 text-center">
                <Flag className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                <p className="text-muted-foreground">No active manual flags.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {flags.map(flag => {
                const color = getRiskColor(flag.severity);
                return (
                  <Card key={flag.id} className={`border-0 shadow-sm rounded-2xl border-l-4 ${color.border}`}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-4">
                          <div className={`w-10 h-10 rounded-xl ${color.bg} flex items-center justify-center shrink-0`}>
                            <Flag className={color.icon} size={20} />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <h3 className="font-semibold">{flag.participant_name}</h3>
                              <Badge className={`${color.bg} ${color.text}`}>{flag.severity}</Badge>
                            </div>
                            <p className="text-sm mt-1">{flag.reason}</p>
                            {flag.notes && (
                              <p className="text-sm text-muted-foreground mt-1">{flag.notes}</p>
                            )}
                            <p className="text-xs text-muted-foreground mt-2">
                              Flagged by {flag.flagged_by_name} • {formatDate(flag.created_at)}
                            </p>
                          </div>
                        </div>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => resolveFlag(flag.id)}
                          className="rounded-full gap-1"
                        >
                          <CheckCircle size={14} /> Resolve
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Participant Detail Dialog */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="rounded-2xl max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope' }}>Risk Assessment Details</DialogTitle>
          </DialogHeader>
          {selectedParticipant && (
            <div className="space-y-4 py-4">
              {/* Header */}
              <div className={`flex items-center gap-4 p-4 rounded-xl ${getRiskColor(selectedParticipant.risk_level).bg}`}>
                <div className="w-14 h-14 rounded-full bg-white/50 flex items-center justify-center">
                  <User className={getRiskColor(selectedParticipant.risk_level).icon} size={28} />
                </div>
                <div>
                  <h3 className="font-semibold text-lg">{selectedParticipant.participant_name}</h3>
                  <div className="flex items-center gap-2 mt-1">
                    <Badge className={`${getRiskColor(selectedParticipant.risk_level).bg} ${getRiskColor(selectedParticipant.risk_level).text}`}>
                      {selectedParticipant.risk_level.toUpperCase()} RISK
                    </Badge>
                    <span className="text-sm">Score: {selectedParticipant.risk_score}/100</span>
                  </div>
                </div>
              </div>

              {/* Risk Score Gauge */}
              <div className="p-4 bg-muted/30 rounded-xl">
                <div className="flex justify-between mb-2">
                  <span className="text-sm font-medium">Risk Level</span>
                  <span className="text-sm font-medium">{selectedParticipant.risk_percentage}%</span>
                </div>
                <Progress value={selectedParticipant.risk_percentage} className="h-3" />
                <div className="flex justify-between mt-1 text-xs text-muted-foreground">
                  <span>Low</span>
                  <span>Medium</span>
                  <span>High</span>
                  <span>Critical</span>
                </div>
              </div>

              {/* Risk Factors */}
              {selectedParticipant.risk_factors?.length > 0 ? (
                <div>
                  <h4 className="font-medium mb-3">Risk Factors ({selectedParticipant.factor_count})</h4>
                  <div className="space-y-3">
                    {selectedParticipant.risk_factors.map((factor, idx) => {
                      const IconComponent = CATEGORY_ICONS[factor.category] || AlertCircle;
                      const color = getRiskColor(factor.severity);
                      return (
                        <div key={factor.label || `factor-${idx}`} className={`p-3 rounded-xl border-l-4 ${color.border} ${color.bg}`}>
                          <div className="flex items-start gap-3">
                            <div className={`w-10 h-10 rounded-lg bg-white/50 flex items-center justify-center shrink-0`}>
                              <IconComponent className={color.icon} size={20} />
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center justify-between">
                                <span className="font-medium">{factor.label}</span>
                                <Badge className={`${color.bg} ${color.text}`}>{factor.severity}</Badge>
                              </div>
                              <p className="text-sm text-muted-foreground mt-1">{factor.detail}</p>
                              <div className="mt-2 p-2 bg-white/50 rounded-lg">
                                <p className="text-xs text-muted-foreground">Recommendation:</p>
                                <p className="text-sm">{factor.recommendation}</p>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <div className="p-6 bg-emerald-50 rounded-xl text-center">
                  <CheckCircle className="w-12 h-12 text-emerald-500 mx-auto mb-2" />
                  <p className="font-medium text-emerald-700">No Risk Factors Detected</p>
                  <p className="text-sm text-emerald-600 mt-1">This participant is in good standing.</p>
                </div>
              )}

              <p className="text-xs text-muted-foreground text-center">
                Assessed at {formatDate(selectedParticipant.assessed_at)}
              </p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDetailDialogOpen(false)} className="rounded-full">Close</Button>
            {selectedParticipant && (
              <Button 
                onClick={() => {
                  setNewFlag({ ...newFlag, participant_id: selectedParticipant.participant_id });
                  setDetailDialogOpen(false);
                  setFlagDialogOpen(true);
                }} 
                className="bg-orange-500 hover:bg-orange-600 text-white rounded-full gap-2"
              >
                <Flag size={16} /> Flag for Follow-up
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Flag Participant Dialog */}
      <Dialog open={flagDialogOpen} onOpenChange={setFlagDialogOpen}>
        <DialogContent className="rounded-2xl max-w-md">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope' }}>Flag Participant</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium mb-1.5 block">Participant *</label>
              <Select value={newFlag.participant_id} onValueChange={(v) => setNewFlag({...newFlag, participant_id: v})}>
                <SelectTrigger className="rounded-xl">
                  <SelectValue placeholder="Select participant..." />
                </SelectTrigger>
                <SelectContent>
                  {dashboard?.participants?.map(p => (
                    <SelectItem key={p.participant_id} value={p.participant_id}>{p.participant_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium mb-1.5 block">Severity</label>
              <Select value={newFlag.severity} onValueChange={(v) => setNewFlag({...newFlag, severity: v})}>
                <SelectTrigger className="rounded-xl">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="critical">Critical</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium mb-1.5 block">Common Risk Factor</label>
              <Select value="" onValueChange={(v) => {
                const labels = {
                  relapse: 'Relapse / Substance Use', missed_checkin: 'Missed Check-In / No-Show',
                  housing_instability: 'Housing Instability / Eviction Risk', employment_loss: 'Employment Loss',
                  mental_health_crisis: 'Mental Health Crisis', legal_violation: 'Legal / Probation Violation',
                  association_risk: 'Risky Associations / Peer Influence', financial_crisis: 'Financial Crisis',
                  family_conflict: 'Family Conflict / Custody Issues', non_compliance: 'Program Non-Compliance',
                  criminal_thinking: 'Criminal Thinking Patterns', anger_management: 'Anger Management Issues',
                  isolation: 'Social Isolation / Withdrawal'
                };
                setNewFlag({...newFlag, reason: (newFlag.reason ? newFlag.reason + '; ' : '') + (labels[v] || v)});
              }}>
                <SelectTrigger className="rounded-xl"><SelectValue placeholder="Select a common reason..." /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="relapse">Relapse / Substance Use</SelectItem>
                  <SelectItem value="missed_checkin">Missed Check-In / No-Show</SelectItem>
                  <SelectItem value="housing_instability">Housing Instability / Eviction Risk</SelectItem>
                  <SelectItem value="employment_loss">Employment Loss</SelectItem>
                  <SelectItem value="mental_health_crisis">Mental Health Crisis</SelectItem>
                  <SelectItem value="legal_violation">Legal / Probation Violation</SelectItem>
                  <SelectItem value="association_risk">Risky Associations / Peer Influence</SelectItem>
                  <SelectItem value="financial_crisis">Financial Crisis</SelectItem>
                  <SelectItem value="family_conflict">Family Conflict / Custody Issues</SelectItem>
                  <SelectItem value="non_compliance">Program Non-Compliance</SelectItem>
                  <SelectItem value="criminal_thinking">Criminal Thinking Patterns</SelectItem>
                  <SelectItem value="anger_management">Anger Management Issues</SelectItem>
                  <SelectItem value="isolation">Social Isolation / Withdrawal</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium mb-1.5 block">Reason * (or type/speak your own)</label>
              <DictationInput 
                value={newFlag.reason}
                onChange={(val) => setNewFlag({...newFlag, reason: val})}
                placeholder="Why are you flagging this participant? (speak or type)"
                rows={2}
                showCleanup={false}
                data-testid="risk-flag-reason"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1.5 block">Notes</label>
              <DictationInput 
                value={newFlag.notes}
                onChange={(val) => setNewFlag({...newFlag, notes: val})}
                placeholder="Additional context... (speak or type)"
                rows={2}
                showCleanup={false}
                data-testid="risk-flag-notes"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFlagDialogOpen(false)} className="rounded-full">Cancel</Button>
            <Button onClick={handleFlagParticipant} className="bg-orange-500 hover:bg-orange-600 text-white rounded-full gap-2">
              <Flag size={16} /> Create Flag
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
